import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import Seo from "@/components/base/Seo";
import SectionHeading from "@/components/feature/SectionHeading";
import FeatureGrid from "@/components/feature/FeatureGrid";
import ProcessSteps from "@/components/feature/ProcessSteps";
import FaqAccordion from "@/components/feature/FaqAccordion";
import VpsPlansSection from "@/pages/vps/components/VpsPlansSection";
import FinalCta from "@/pages/home/components/FinalCta";
import { vpsPlans, vpsTechnical, vpsUseCases, vpsFaqs } from "@/mocks/vps";
import { PLAN_IMAGE_VPS } from "@/constants/site";

const migrationSteps = [
  {
    icon: "ri-chat-3-line",
    title: "Fale com um especialista",
    description: "Conte o que o seu projeto precisa e como ele funciona hoje.",
  },
  {
    icon: "ri-search-eye-line",
    title: "Dimensionamos o VPS",
    description: "Avaliamos recursos e indicamos o plano mais adequado para você.",
  },
  {
    icon: "ri-exchange-2-line",
    title: "Cuidamos da migração",
    description: "Movemos o seu ambiente para o VPS com acompanhamento da nossa equipe.",
  },
  {
    icon: "ri-checkbox-circle-line",
    title: "Acompanhamos os primeiros dias",
    description: "Seguimos por perto para garantir estabilidade na nova estrutura.",
  },
];

export default function Vps() {
  return (
    <SiteLayout>
      <Seo
        title="VPS e Servidores Virtuais com Recursos Dedicados | NXHOST"
        description="Planos de VPS com recursos dedicados, armazenamento NVMe, IP dedicado e backups. Escolha o VPS ideal para e-commerce, sistemas e aplicações que exigem desempenho."
        keywords="VPS, servidor virtual, VPS Brasil, VPS NVMe, servidor dedicado, hospedagem VPS"
        path="/vps"
        titleEn="VPS and Virtual Servers with Dedicated Resources | NXHOST"
        titleEs="VPS y Servidores Virtuales con Recursos Dedicados | NXHOST"
        descriptionEn="VPS plans with dedicated resources, NVMe storage, dedicated IP and backups. Choose the ideal VPS for e-commerce, systems and applications that demand performance."
        descriptionEs="Planes de VPS con recursos dedicados, almacenamiento NVMe, IP dedicada y copias de seguridad. Elige el VPS ideal para e-commerce, sistemas y aplicaciones que exigen rendimiento."
        plans={vpsPlans.map((plan) => ({
          name: plan.name,
          price: plan.price,
          url: "https://nxhost.com.br/vps",
          description: plan.audience,
          image: PLAN_IMAGE_VPS,
          category: "VPS e servidores virtuais",
        }))}
        breadcrumbs={[
          { name: "Início", path: "/" },
          { name: "VPS", path: "/vps" },
        ]}
        faqs={vpsFaqs}
      />

      <PageHero
        badge="VPS e servidores virtuais"
        badgeIcon="ri-cloud-line"
        title="Mais controle. Mais recursos. Mais possibilidades."
        subtitle="Para projetos que precisam de uma infraestrutura mais robusta, oferecemos VPS com recursos dedicados, liberdade de configuração e desempenho para crescer."
        crumbs={[{ label: "Início", to: "/" }, { label: "VPS" }]}
      >
        <Reveal delay={220}>
          <div className="rounded-2xl border border-background-50/10 bg-foreground-900/60 p-5 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-background-400">
                Painel do VPS
              </span>
              <span className="flex items-center gap-1.5 text-[11px] font-medium text-secondary-400">
                <span className="h-2 w-2 rounded-full bg-secondary-400"></span>
                Online
              </span>
            </div>

            <div className="mt-4 flex items-end gap-1">
              <span className="text-sm text-background-400">a partir de</span>
            </div>
            <div className="mt-1 flex items-end gap-1">
              <span className="text-sm font-medium text-background-300">R$</span>
              <span className="font-heading text-5xl font-bold text-background-50">69,90</span>
              <span className="mb-2 text-sm text-background-300">/mês</span>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-background-50/10 bg-background-50/5 p-3">
                <p className="text-[11px] uppercase tracking-wide text-background-400">vCPU</p>
                <p className="mt-1 font-heading text-lg font-bold text-background-50">1 a 4</p>
              </div>
              <div className="rounded-lg border border-background-50/10 bg-background-50/5 p-3">
                <p className="text-[11px] uppercase tracking-wide text-background-400">Memória</p>
                <p className="mt-1 font-heading text-lg font-bold text-background-50">3 a 32 GB</p>
              </div>
            </div>
          </div>
        </Reveal>
      </PageHero>

      <VpsPlansSection />

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Recursos técnicos"
            badgeIcon="ri-cpu-line"
            badgeTone="accent"
            title="Tecnologia para o seu projeto render mais."
            description="Cada VPS da NXHOST é montado com componentes pensados para desempenho, segurança e disponibilidade."
          />
          <div className="mt-12">
            <FeatureGrid items={vpsTechnical} columns={3} accent="accent" />
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-foreground-950 py-16 text-background-50 md:py-24">
        <div className="absolute inset-0 nx-grid opacity-25"></div>
        <div className="nx-glow absolute bottom-0 right-1/4 h-[420px] w-[420px] animate-pulseGlow"></div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Ideal para"
            badgeIcon="ri-rocket-2-line"
            title="O VPS certo para cada tipo de aplicação."
            description="Do e-commerce que não pode parar aos sistemas internos que exigem processamento constante."
            tone="dark"
          />
          <div className="mt-12">
            <FeatureGrid items={vpsUseCases} tone="dark" columns={4} accent="accent" />
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Migração de VPS"
            badgeIcon="ri-exchange-2-line"
            badgeTone="secondary"
            title="Migramos o seu ambiente com cuidado."
            description="Traga o seu projeto de outro provedor com o acompanhamento da nossa equipe em cada etapa."
          />
          <div className="mt-12">
            <ProcessSteps steps={migrationSteps} />
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Dúvidas frequentes"
            badgeIcon="ri-question-line"
            title="Perguntas sobre VPS."
          />
          <div className="mt-12">
            <FaqAccordion items={vpsFaqs} />
          </div>
        </div>
      </section>

      <FinalCta />
    </SiteLayout>
  );
}